import { UseMutationResult } from 'react-query';
import {
  ReportConfig,
  AvailableReportsData,
  StudentReportData,
  ReportOption,
  CourseSectionReportData,
  TeacherReportData,
  CourseReportData,
  DistrictReportData,
} from '../types';
import { success, error as showError } from '../../../utils/toast';

interface StudentSkillLevelReportDependencies {
  getStudentSkillLevelReportMutation: UseMutationResult<
    any,
    Error,
    {
      studentId: number;
      curriculumId: number;
      studentName?: string;
      curriculumName?: string;
    }
  >;
  getStudentSkillLevelReportsBatchMutation: UseMutationResult<
    any,
    Error,
    {
      reports: { studentId: number; curriculumId: number }[];
    }
  >;
}

interface SectionSkillLevelReportDependencies {
  getSectionSkillLevelReportMutation: UseMutationResult<
    any,
    Error,
    {
      sectionId: number;
      courseId: number;
      sectionName?: string;
      courseName?: string;
    }
  >;
}

interface TeacherSkillLevelReportDependencies {
  getTeacherSkillLevelReportMutation: UseMutationResult<
    any,
    Error,
    {
      teacherId: number;
      curriculumId: number;
    }
  >;
  getTeacherSkillLevelReportsBatchMutation: UseMutationResult<
    any,
    Error,
    {
      reports: { teacherId: number; curriculumId: number }[];
    }
  >;
}

interface DistrictSkillLevelReportDependencies {
  getDistrictSkillLevelReportMutation: UseMutationResult<
    any,
    Error,
    {
      districtId: number;
      curriculumId: number;
      districtName?: string;
      curriculumName?: string;
    }
  >;
}
interface CourseSkillLevelReportDependencies {
  getCourseSkillLevelReportMutation: UseMutationResult<
    any,
    Error,
    {
      courseId: number;
    }
  >;
  getCourseSkillLevelReportsBatchMutation: UseMutationResult<
    any,
    Error,
    {
      courseIds: number[];
    }
  >;
}

interface ReportResult {
  label: string;
  result: unknown;
}

const showReportResultToasts = (results: ReportResult[], name: string) => {
  const successes: string[] = [];
  const failures: string[] = [];

  results.forEach(({ label, result }) => {
    if (result && typeof result === 'object' && 'error' in result) {
      failures.push(label);
    } else {
      successes.push(label);
    }
  });

  if (successes.length > 0) {
    const successMessage =
      successes.length === 1
        ? `Successfully generated report for ${name} (${successes[0]})`
        : `Successfully generated ${successes.length} reports for ${name}`;
    success(successMessage);
  }

  if (failures.length > 0) {
    const failedItems = failures.join(', ');
    const errorMessage =
      failures.length === 1
        ? `Failed to generate report for: ${failedItems}`
        : `Failed to generate ${failures.length} reports for: ${failedItems}`;
    showError(errorMessage);
  }
};

export const createStudentSkillLevelReportConfigs = (
  availableReportsData: AvailableReportsData,
  dependencies: StudentSkillLevelReportDependencies,
): ReportConfig[] => {
  const configs: ReportConfig[] = [];

  if (!availableReportsData?.skill_level_reports?.student_reports) {
    return configs;
  }

  const studentReports = availableReportsData.skill_level_reports.student_reports;
  const studentOptions = Object.values(studentReports).map(
    (student: StudentReportData) => ({
      id: student.student_id,
      label: student.student_name,
      ...student,
    }),
  );

  if (studentOptions.length > 0) {
    configs.push({
      id: 'skill_level_student',
      name: 'Student Skill Level Report',
      option_name: 'student',
      options: studentOptions,
      multiple: false, // Only select one student
      selections: (selectedStudent: ReportOption | ReportOption[]) => {
        // Return child config based on selected student
        const student = selectedStudent as unknown as StudentReportData;

        if (!student || !student.options_dict) return null;

        const curriculums = Object.values(student.options_dict).map((curriculum) => ({
          id: curriculum.curriculum_id,
          label: curriculum.curriculum_name,
          ...curriculum,
        }));

        return {
          option_name: student.options_name || 'curriculums',
          options: curriculums,
          multiple: true,
          selections: null, // This is the deepest level
          onSubmit: async (context) => {
            // context.selections[0] = selected student
            // context.selections[1] = selected curriculums
            const selectedStudent = context.selections[0] as unknown as StudentReportData;
            const selectedCurriculums = (context.selections[1] || []) as ReportOption[];

            try {
              const reportItems = selectedCurriculums.map((curriculum) => ({
                studentId: selectedStudent.student_id,
                curriculumId: Number(curriculum.id),
              }));

              if (reportItems.length > 1) {
                await dependencies.getStudentSkillLevelReportsBatchMutation.mutateAsync({
                  reports: reportItems,
                });
              } else if (reportItems.length === 1) {
                const [reportItem] = reportItems;
                const curriculum = selectedCurriculums[0];
                const result =
                  await dependencies.getStudentSkillLevelReportMutation.mutateAsync({
                    studentId: reportItem.studentId,
                    curriculumId: reportItem.curriculumId,
                    studentName: selectedStudent.student_name,
                    curriculumName: curriculum.label,
                  });
                showReportResultToasts(
                  [{ label: curriculum.label, result }],
                  selectedStudent.student_name,
                );
              }

              // TODO: Add navigation to history view in future ticket
            } catch (error) {
              console.error('Error generating student skill level reports:', error);
              showError(
                'Failed to generate student skill level reports. Please try again.',
              );
            }
          },
        };
      },
    });
  }

  return configs;
};

const normalizeTeacherSelections = (
  selectedTeachers: ReportOption | ReportOption[],
): TeacherReportData[] => {
  const teachers = Array.isArray(selectedTeachers)
    ? selectedTeachers
    : [selectedTeachers];

  return teachers.filter(Boolean) as unknown as TeacherReportData[];
};

const getCurriculumOptionsForTeachers = (
  teachers: TeacherReportData[],
): ReportOption[] => {
  const curriculumMap = new Map<number, ReportOption>();

  teachers.forEach((teacher) => {
    if (!teacher?.options_dict) {
      return;
    }

    Object.values(teacher.options_dict).forEach((curriculum) => {
      curriculumMap.set(curriculum.curriculum_id, {
        id: curriculum.curriculum_id,
        label: curriculum.curriculum_name,
        ...curriculum,
      });
    });
  });

  return Array.from(curriculumMap.values());
};

const teacherHasCurriculum = (
  teacher: TeacherReportData,
  curriculumId: number,
): boolean =>
  Object.values(teacher.options_dict || {}).some(
    (curriculum) => curriculum.curriculum_id === curriculumId,
  );

export const createTeacherSkillLevelReportConfigs = (
  availableReportsData: AvailableReportsData,
  dependencies: TeacherSkillLevelReportDependencies,
): ReportConfig[] => {
  const configs: ReportConfig[] = [];

  if (!availableReportsData?.skill_level_reports?.teacher_reports) {
    return configs;
  }

  const teacherReports = availableReportsData.skill_level_reports.teacher_reports;
  const teacherOptions = Object.values(teacherReports).map(
    (teacher: TeacherReportData) => ({
      id: teacher.teacher_id,
      label: teacher.teacher_name,
      ...teacher,
    }),
  );

  if (teacherOptions.length > 0) {
    configs.push({
      id: 'skill_level_teacher',
      name: 'Teacher Skill Level Report',
      option_name: 'teacher',
      options: teacherOptions,
      multiple: true,
      selections: (selectedTeachers: ReportOption | ReportOption[]) => {
        const teachers = normalizeTeacherSelections(selectedTeachers);

        if (teachers.length === 0) {
          return null;
        }

        const curriculums = getCurriculumOptionsForTeachers(teachers);

        if (curriculums.length === 0) {
          return null;
        }

        return {
          option_name: teachers[0].options_name || 'curriculums',
          options: curriculums,
          multiple: true,
          selections: null,
          onSubmit: async (context) => {
            const selectedTeachers = normalizeTeacherSelections(
              context.selections[0] || [],
            );
            const selectedCurriculums = (context.selections[1] || []) as ReportOption[];

            try {
              const reportItems = selectedTeachers.flatMap((teacher) =>
                selectedCurriculums
                  .filter((curriculum) =>
                    teacherHasCurriculum(teacher, Number(curriculum.id)),
                  )
                  .map((curriculum) => ({
                    teacherId: teacher.teacher_id,
                    curriculumId: Number(curriculum.id),
                  })),
              );

              if (reportItems.length > 1) {
                await dependencies.getTeacherSkillLevelReportsBatchMutation.mutateAsync({
                  reports: reportItems,
                });
              } else if (reportItems.length === 1) {
                const [reportItem] = reportItems;
                await dependencies.getTeacherSkillLevelReportMutation.mutateAsync(
                  reportItem,
                );
              }
            } catch (error) {
              console.error('Error generating teacher skill level reports:', error);
              showError(
                'Failed to generate teacher skill level reports. Please try again.',
              );
            }
          },
        };
      },
    });
  }

  return configs;
};

export const createDistrictSkillLevelReportConfigs = (
  availableReportsData: AvailableReportsData,
  dependencies: DistrictSkillLevelReportDependencies,
): ReportConfig[] => {
  const configs: ReportConfig[] = [];

  if (!availableReportsData?.skill_level_reports?.district_reports) {
    return configs;
  }

  const districtReports = availableReportsData.skill_level_reports.district_reports;
  const districtOptions = Object.values(districtReports).map(
    (district: DistrictReportData) => ({
      id: district.district_id,
      label: district.district_name,
      ...district,
    }),
  );

  if (districtOptions.length > 0) {
    configs.push({
      id: 'skill_level_district',
      name: 'District Skill Level Report',
      option_name: 'district',
      options: districtOptions,
      multiple: false,
      selections: (selectedDistrict: ReportOption | ReportOption[]) => {
        const district = selectedDistrict as unknown as DistrictReportData;

        if (!district || !district.options_dict) {
          return null;
        }

        const curriculums = Object.values(district.options_dict).map((curriculum) => ({
          id: curriculum.curriculum_id,
          label: curriculum.curriculum_name,
          ...curriculum,
        }));

        return {
          option_name: district.options_name || 'curriculums',
          options: curriculums,
          multiple: true,
          selections: null,
          onSubmit: async (context) => {
            const selectedDistrict = context
              .selections[0] as unknown as DistrictReportData;
            const selectedCurriculums = (context.selections[1] || []) as ReportOption[];

            try {
              // Use mutation for each selected curriculum
              // This ensures the mutation's onSuccess callback runs and invalidates the past reports query
              const reportPromises = selectedCurriculums.map(async (curriculum) => {
                const result =
                  await dependencies.getDistrictSkillLevelReportMutation.mutateAsync({
                    districtId: selectedDistrict.district_id,
                    curriculumId: Number(curriculum.id),
                    districtName: selectedDistrict.district_name,
                    curriculumName: curriculum.label,
                  });
                return { label: curriculum.label, result };
              });

              await Promise.all(reportPromises);
            } catch (error) {
              console.error('Error generating district skill level reports:', error);
              showError(
                'Failed to generate district skill level reports. Please try again.',
              );
            }
          },
        };
      },
    });
  }

  return configs;
};

export const createCourseSkillLevelReportConfigs = (
  availableReportsData: AvailableReportsData,
  dependencies: CourseSkillLevelReportDependencies,
): ReportConfig[] => {
  const configs: ReportConfig[] = [];

  if (!availableReportsData?.skill_level_reports?.course_reports) {
    return configs;
  }

  const courseReports = availableReportsData.skill_level_reports.course_reports;
  const courseOptions = Object.values(courseReports).map((course: CourseReportData) => ({
    id: course.course_id,
    label: course.course_name,
    ...course,
  }));

  if (courseOptions.length > 0) {
    configs.push({
      id: 'skill_level_course',
      name: 'School Skill Level Report',
      option_name: 'School',
      options: courseOptions,
      multiple: true,
      selections: null,
      onSubmit: async (context) => {
        const selectedCourses = (context.selections[0] || []) as ReportOption[];

        try {
          const courseIds = selectedCourses.map((course) =>
            Number((course as unknown as CourseReportData).course_id),
          );

          if (courseIds.length > 1) {
            await dependencies.getCourseSkillLevelReportsBatchMutation.mutateAsync({
              courseIds,
            });
          } else if (courseIds.length === 1) {
            await dependencies.getCourseSkillLevelReportMutation.mutateAsync({
              courseId: courseIds[0],
            });
          }
        } catch (error) {
          console.error('Error generating school skill level reports:', error);
          showError('Failed to generate school skill level reports. Please try again.');
        }
      },
    });
  }

  return configs;
};

// Placeholder for future section reports
export const createSectionSkillLevelReportConfigs = (
  availableReportsData: AvailableReportsData,
  dependencies: SectionSkillLevelReportDependencies,
): ReportConfig[] => {
  const configs: ReportConfig[] = [];

  if (!availableReportsData?.skill_level_reports?.section_reports) {
    return configs;
  }

  const sectionReports = availableReportsData.skill_level_reports.section_reports;
  const courseOptions = Object.values(sectionReports).map(
    (course: CourseSectionReportData) => ({
      id: course.course_id,
      label: course.course_name,
      ...course,
    }),
  );

  if (courseOptions.length > 0) {
    configs.push({
      id: 'skill_level_section',
      name: 'Class Skill Level Report',
      option_name: 'School',
      options: courseOptions,
      multiple: false,
      selections: (selectedCourse: ReportOption | ReportOption[]) => {
        const course = selectedCourse as unknown as CourseSectionReportData;
        const sectionOptions = Object.values(course.options_dict).map((section) => ({
          id: section.section_id,
          label: section.section_name,
          ...section,
        }));

        return {
          option_name: course.options_name || 'Sections',
          options: sectionOptions,
          multiple: true,
          selections: null,
          onSubmit: async (context) => {
            const selectedCourse = context
              .selections[0] as unknown as CourseSectionReportData;
            const selectedSections = (context.selections[1] || []) as ReportOption[];

            try {
              // Use mutation for each selected section
              // This ensures the mutation's onSuccess callback runs and invalidates the past reports query
              const reportPromises = selectedSections.map(async (section) => {
                const result =
                  await dependencies.getSectionSkillLevelReportMutation.mutateAsync({
                    sectionId: Number(section.id),
                    courseId: Number(course.course_id),
                    sectionName: section.label,
                    courseName: course.course_name,
                  });
                return { label: course.course_name, result };
              });

              const results = await Promise.all(reportPromises);
              showReportResultToasts(results, selectedCourse.course_name);
            } catch (error) {
              console.error('Error generating class skill level reports:', error);
              showError(
                'Failed to generate class skill level reports. Please try again.',
              );
            }
          },
        };
      },
    });
  }

  return configs;
};
